import { MW_ELEMENTARY_API_KEY, MW_ELEMENTARY_API_URL } from '../config/constants';

const CACHE_PREFIX = 'mw_def_';
const CACHE_DURATION_DAYS = 30;

export interface WordDefinition {
  word: string;
  partOfSpeech?: string;
  definition: string;
  pronunciations?: string[];
  audioFile?: string; // Audio filename for pronunciation
}

interface CachedDefinition {
  definition: WordDefinition;
  expiresAt: string;
}

interface MWApiEntry {
  hwi?: {
    hw?: string;
    prs?: Array<{
      mw: string;
      sound?: {
        audio: string;
      };
    }>;
  };
  fl?: string;
  shortdef?: string[];
}

/**
 * Clean and normalize word for lookup
 */
function normalizeWord(word: string): string {
  return word.toLowerCase().trim().replace(/[^a-z'-]/g, '');
}

/**
 * Build audio URL for pronunciation
 * Based on Merriam-Webster API documentation
 */
export function getAudioUrl(audioFile: string): string {
  if (!audioFile) return '';

  // Determine subdirectory based on filename
  let subdirectory: string;
  if (audioFile.startsWith('bix')) {
    subdirectory = 'bix';
  } else if (audioFile.startsWith('gg')) {
    subdirectory = 'gg';
  } else if (/^[0-9_]/.test(audioFile)) {
    subdirectory = 'number';
  } else {
    subdirectory = audioFile.charAt(0);
  }

  // For Elementary Dictionary, use en/us
  return `https://media.merriam-webster.com/audio/prons/en/us/mp3/${subdirectory}/${audioFile}.mp3`;
}

/**
 * Get cached definition if available
 */
function getCachedDefinition(word: string): WordDefinition | null {
  try {
    const cached = localStorage.getItem(CACHE_PREFIX + word);
    if (!cached) return null;

    const data: CachedDefinition = JSON.parse(cached);
    const expiresAt = new Date(data.expiresAt);

    if (expiresAt > new Date()) {
      return data.definition;
    } else {
      localStorage.removeItem(CACHE_PREFIX + word);
      return null;
    }
  } catch {
    return null;
  }
}

/**
 * Cache definition
 */
function cacheDefinition(word: string, definition: WordDefinition): void {
  try {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + CACHE_DURATION_DAYS);

    const data: CachedDefinition = {
      definition,
      expiresAt: expiresAt.toISOString()
    };

    localStorage.setItem(CACHE_PREFIX + word, JSON.stringify(data));
  } catch {
    // Cache write failed silently
  }
}

/**
 * Parse MW Elementary API response
 */
function parseDefinition(data: unknown, originalWord: string): WordDefinition | null {
  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }

  const entry = data[0];

  // Check if it's a suggestion list (no definition found)
  if (typeof entry === 'string') {
    return null;
  }

  const apiEntry = entry as MWApiEntry;

  // Extract the first definition
  const shortdef = apiEntry.shortdef?.[0];
  const partOfSpeech = apiEntry.fl; // functional label (noun, verb, etc.)
  const word = apiEntry.hwi?.hw?.replace(/\*/g, '') || originalWord; // headword

  if (!shortdef) {
    return null;
  }

  // Extract audio file from first pronunciation that has audio
  const audioFile = apiEntry.hwi?.prs?.find(pr => pr.sound?.audio)?.sound?.audio;

  return {
    word,
    partOfSpeech,
    definition: shortdef,
    pronunciations: apiEntry.hwi?.prs?.map(pr => pr.mw) || [],
    audioFile
  };
}

/**
 * Fetch definition from Merriam-Webster Elementary Dictionary
 */
async function fetchDefinition(word: string): Promise<WordDefinition> {
  const normalized = normalizeWord(word);

  if (!normalized || normalized.length < 2) {
    throw new Error('Word too short');
  }

  try {
    const url = `${MW_ELEMENTARY_API_URL}/${encodeURIComponent(normalized)}?key=${MW_ELEMENTARY_API_KEY}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    const definition = parseDefinition(data, normalized);

    if (!definition) {
      throw new Error('No definition found');
    }

    return definition;
  } catch (error) {
    throw error;
  }
}

/**
 * Get word definition (with caching)
 */
export async function getWordDefinition(word: string): Promise<WordDefinition> {
  const normalized = normalizeWord(word);

  // Check cache first
  const cached = getCachedDefinition(normalized);
  if (cached) {
    return cached;
  }

  // Fetch from API
  const definition = await fetchDefinition(normalized);

  // Cache for future use
  if (definition) {
    cacheDefinition(normalized, definition);
  }

  return definition;
}

/**
 * Clear all cached definitions
 */
export function clearDefinitionCache(): void {
  const keys = Object.keys(localStorage);
  keys.forEach(key => {
    if (key.startsWith(CACHE_PREFIX)) {
      localStorage.removeItem(key);
    }
  });
}
